import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Button, IconButton, Tab, TabList, Tabs, Typography } from "@mui/joy";
import { Box } from "@mui/material";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Venue } from "../../entities/venue";
import { compatibleSources } from "../../global";
import { useGigs } from "../../redux/gig/gigHooks";
import { useProjects } from "../../redux/project/projectHooks";
import { useVenues } from "../../redux/venue/venueHooks";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useDispatch } from "react-redux";
import {
  setFormOpen,
  setSelectedGigIDs,
  setTmpGig,
} from "../../reducers/rhapsody";

export default function Calendar({
  projectID,
  venueID,
  today,
  contractorID,
  disableCreate,
}: {
  projectID?: number;
  venueID?: number;
  contractorID?: number;
  today?: boolean;
  disableCreate?: boolean;
}) {
  const calendarRef = useRef();
  const calendarEl: any = calendarRef?.current;
  const tzName = moment.tz.guess();
  const dispatch = useDispatch();
  const [key, setKey] = useState(0);

  const { gigs, gigsMap } = useGigs();
  const { projects } = useProjects();
  const { venues } = useVenues();

  const [week] = useLocalStorage("WeeksStartOn", "0");

  let _gigs = [...gigs];
  if (projectID) {
    _gigs = _gigs.filter((g) => g.projectID === projectID);
  }

  if (venueID) {
    _gigs = _gigs.filter((g) => g.venueID === venueID);
  }

  if (contractorID) {
    _gigs = _gigs.filter((g) => g.contractorID === contractorID);
  }

  useEffect(() => {
    if (calendarRef.current) {
      setKey((k) => k + 1);
    }
  }, [calendarRef]);

  const getEventsFromGigs = () => {
    const ret: any[] = [];
    _gigs?.forEach((gig) => {
      const venue = venues?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.venueID
      );
      const tz = venue?.tzName;
      // FullCalendar has a single global timeZone (here "local"). Feeding raw
      // UTC instants would position the block at the viewer's browser hour
      // while the overlay (RenderEventContent) reads the venue-local hour, so
      // the block and its label disagree. Hand FullCalendar the venue-local
      // WALL CLOCK as an offset-less string so the block lands at the same
      // hour the label shows.
      ret.push({
        id: gig.id,
        title: gig.title,
        gig,
        start: gig.from(tz).format("YYYY-MM-DDTHH:mm:ss"),
        end: gig.to(tz).format("YYYY-MM-DDTHH:mm:ss"),
        color: gig.publicToken ? "#824CDE" : "#824CDE",
        // color: project?,
        allDay: gig.noTime(tz),
        editable: false,
        eventStartEditable: false,
      });
    });

    return ret;
  };

  const handleDateSelect = (selectInfo) => {
    if (selectInfo.view.type !== "dayGridMonth") {
      // FullCalendar runs in "local" timeZone, so startStr/endStr are
      // offset-less wall-clock strings for the slot the user dragged. If this
      // calendar is scoped to a single venue, interpret that wall clock in the
      // venue's tz so the stored UTC matches the slot the user sees. Otherwise
      // there is no venue context yet: seed the wall clock as browser-local;
      // FormGig re-interprets the same wall clock once a venue is chosen.
      const venue = venueID
        ? venues?.find((v) => v.sourceRecordID == venueID || v.id === venueID)
        : undefined;
      const tz = venue?.tzName ?? moment.tz.guess();
      dispatch(
        setTmpGig({
          dateFromUTC: moment.tz(selectInfo.startStr, tz).utc().format(),
          dateToUTC: moment.tz(selectInfo.endStr, tz).utc().format(),
        })
      );
      dispatch(setFormOpen({ isOpen: true, formID: "gig" }));
    }
  };

  function getSelectedDate() {
    return calendarEl?.getApi().view.title;
  }

  function getInitialDate() {
    if (today) return moment().toDate();
    // any upcoming?
    const upcomings = _gigs.filter((w) =>
      moment(w.dateFromUTC).isAfter(moment())
    );
    if (upcomings.length > 0) {
      return moment(upcomings[0].dateFromUTC).toDate();
    }

    const _workSessions = JSON.parse(JSON.stringify(_gigs));
    _workSessions.sort(
      (a, b) =>
        moment(a.dateFromUTC).valueOf() - moment(b.dateFromUTC).valueOf()
    );

    if (_workSessions.length > 0) {
      return moment(_workSessions[0].dateFromUTC).toDate();
    }

    return moment().toDate();
  }

  return (
    <Box
      className="prevent-select"
      sx={{
        flex: 1,
        display: "flex",
        gap: 1,
        flexDirection: "column",
        minHeight: 800,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => {
              const calendarEl: any = calendarRef.current;
              calendarEl?.getApi()?.today();
              setKey((k) => k + 1);
            }}
            size="sm"
            color="neutral"
            variant="soft"
          >
            Today
          </Button>
          <IconButton
            onClick={() => {
              const calendarEl: any = calendarRef.current;
              calendarEl?.getApi()?.prev();
              setKey((k) => k + 1);
            }}
            size="sm"
            color="neutral"
            variant="soft"
          >
            <i className="fa-regular fa-chevron-left"></i>
          </IconButton>
          <IconButton
            onClick={() => {
              const calendarEl: any = calendarRef.current;
              calendarEl?.getApi()?.next();
              setKey((k) => k + 1);
            }}
            size="sm"
            color="neutral"
            variant="soft"
          >
            <i className="fa-regular fa-chevron-right"></i>
          </IconButton>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography level="h6">{getSelectedDate()}</Typography>
          <Typography level="body2">{tzName}</Typography>
        </Box>
        <Tabs
          defaultValue={"week"}
          onChange={(e, v) => {
            const calendarEl: any = calendarRef.current;
            switch (v) {
              case "month":
                calendarEl?.getApi()?.changeView("dayGridMonth");
                break;
              case "week":
                calendarEl?.getApi()?.changeView("timeGridWeek");
                break;
            }
            setKey((k) => k + 1);
          }}
          size="sm"
          variant="soft"
          sx={{ borderRadius: 3 }}
        >
          <TabList variant="outlined" color="neutral">
            <Tab value="month">Month</Tab>
            <Tab value="week">Week</Tab>
          </TabList>
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <FullCalendar
          height="100%"
          timeZone="local"
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: "",
            center: "",
            right: "",
          }}
          ref={calendarRef as any}
          weekNumbers
          selectable={!disableCreate}
          initialDate={getInitialDate()}
          select={!disableCreate ? handleDateSelect : undefined}
          firstDay={parseInt(week ?? "0")}
          initialView={"timeGridWeek"}
          selectMirror
          dayMaxEvents
          allDaySlot={true}
          weekends
          events={getEventsFromGigs()}
          eventClick={(e) => {
            const gigID = parseInt(e.event.id);
            if (!gigsMap) return <Box />;
            const gig = gigsMap[gigID];
            if (gig.sourceID === 420) {
              dispatch(
                setSelectedGigIDs({
                  id: gig.id,
                  sourceID: gig.sourceID,
                  sourceRecordID: gig.sourceRecordID,
                })
              );
              dispatch(setFormOpen({ isOpen: true, formID: "gig" }));
            } else {
              window.open(gig.getInviteURL());
            }
          }}
          eventContent={(e) => {
            const gigID = parseInt(e.event.id);
            if (!gigID) return <Box>New Service</Box>;
            if (!gigsMap) return <Box />;
            const gig = gigsMap[gigID];
            const project = projects?.find(
              (e) =>
                compatibleSources(e.sourceID, gig.sourceID) &&
                e.sourceRecordID == gig.projectID
            );

            const venue = venues?.find(
              (e) =>
                compatibleSources(e.sourceID, gig.sourceID) &&
                e.sourceRecordID == gig.venueID
            );
            return (
              <RenderEventContent gig={gig} project={project} venue={venue} />
            );
          }}
          eventDurationEditable={false}
        />
      </Box>
    </Box>
  );
}

let eventGuid = 0;

export function createEventId() {
  return String(eventGuid++);
}

export const RenderEventContent = ({
  gig,
  project,
  venue,
}: {
  gig?: Gig;
  project?: Project;
  venue?: Venue;
}) => {
  const tz = venue?.tzName;
  const tzAbbr = gig?.from(tz).format("z");
  const date = !gig.noTime(tz)
    ? `${gig?.startTime(tz)} - ${gig?.endTime(tz)} ${tzAbbr}: `
    : "";
  return (
    <Box
      sx={{
        overflow: "hidden",
        width: "100%",
        cursor: "pointer",
      }}
    >
      {date}
      {gig?.title} - {project?.name}
      <br />
      <i>@{venue?.name}</i>
    </Box>
  );
};
